export const EPSILON = 1e-6;
/**
 * Normalizes a 3D vector to unit length.
 * Fallback to (0, 1, 0) if vector is nearly zero.
 */
export function normalizeVec3(v) {
    const lenSq = v.x * v.x + v.y * v.y + v.z * v.z;
    if (lenSq <= EPSILON * EPSILON) {
        return { x: 0, y: 1, z: 0 };
    }
    const len = Math.sqrt(lenSq);
    return {
        x: v.x / len,
        y: v.y / len,
        z: v.z / len,
    };
}
/**
 * Computes dot product of two 3D vectors.
 */
export function dotVec3(a, b) {
    return a.x * b.x + a.y * b.y + a.z * b.z;
}
/**
 * Computes vector length.
 */
export function lengthVec3(v) {
    return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
}
/**
 * Multiplies vector by scalar.
 */
export function scaleVec3(v, s) {
    return { x: v.x * s, y: v.y * s, z: v.z * s };
}
/**
 * Adds two vectors.
 */
export function addVec3(a, b) {
    return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}
/**
 * Subtracts vector b from vector a.
 */
export function subVec3(a, b) {
    return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}
/**
 * Clamps a number between min and max.
 */
export function clamp(val, min, max) {
    return Math.max(min, Math.min(max, val));
}
/**
 * Converts radians to degrees.
 */
export function radToDeg(rad) {
    return (rad * 180) / Math.PI;
}
/**
 * Converts degrees to radians.
 */
export function degToRad(deg) {
    return (deg * Math.PI) / 180;
}
/**
 * D2. Calculates impact angle between incoming unit trajectory direction d and unit surface normal n.
 *
 * Formula:
 *   cos(theta) = clamp(|d . n|, 0, 1)
 *   theta = acos(cos(theta))
 *
 * Theta is 0° when moving exactly perpendicular to the surface.
 * Obliquity = 90° - theta.
 */
export function calculateImpactAngle(direction, normal) {
    const d = normalizeVec3(direction);
    const n = normalizeVec3(normal);
    const rawDot = dotVec3(d, n);
    const isBackface = rawDot > 0;
    const absDot = Math.abs(rawDot);
    const cosTheta = clamp(absDot, 0, 1);
    const incidenceRad = Math.acos(cosTheta);
    const incidenceDeg = radToDeg(incidenceRad);
    const obliquityDeg = Math.max(0, 90 - incidenceDeg);
    return {
        incidenceRad,
        incidenceDeg,
        obliquityDeg,
        cosTheta,
        isBackface,
    };
}
/**
 * D3. Calculates effective geometric line-of-sight thickness through a flat homogeneous plate.
 *
 * Formula:
 *   t_effective = t / max(|d . n|, epsilon)
 */
export function calculateEffectiveThickness(nominalThicknessMm, cosTheta, epsilon = EPSILON) {
    const safeCos = Math.max(cosTheta, epsilon);
    return nominalThicknessMm / safeCos;
}
/**
 * E3. Calculates ideal geometric reflection vector r for a projectile hitting a surface.
 *
 * Formula:
 *   r = d - 2 * (d . n) * n
 *
 * Where d and n are unit vectors.
 */
export function calculateReflectionVector(direction, normal) {
    const d = normalizeVec3(direction);
    const n = normalizeVec3(normal);
    const dDotN = dotVec3(d, n);
    // r = d - 2 * (d . n) * n
    const r = {
        x: d.x - 2 * dDotN * n.x,
        y: d.y - 2 * dDotN * n.y,
        z: d.z - 2 * dDotN * n.z,
    };
    return normalizeVec3(r);
}
/**
 * Transforms a local vector to world space given hull yaw, pitch, roll.
 */
export function transformLocalToWorld(v, yawRad, pitchRad = 0, rollRad = 0) {
    // 1. Roll around Z
    const cosR = Math.cos(rollRad);
    const sinR = Math.sin(rollRad);
    const x1 = v.x * cosR - v.y * sinR;
    const y1 = v.x * sinR + v.y * cosR;
    const z1 = v.z;
    // 2. Pitch around X
    const cosP = Math.cos(pitchRad);
    const sinP = Math.sin(pitchRad);
    const x2 = x1;
    const y2 = y1 * cosP - z1 * sinP;
    const z2 = y1 * sinP + z1 * cosP;
    // 3. Yaw around Y
    const cosY = Math.cos(yawRad);
    const sinY = Math.sin(yawRad);
    const x3 = x2 * cosY + z2 * sinY;
    const y3 = y2;
    const z3 = -x2 * sinY + z2 * cosY;
    return { x: x3, y: y3, z: z3 };
}
/**
 * Transforms a world direction vector into local space.
 */
export function transformWorldToLocal(v, yawRad, pitchRad = 0, rollRad = 0) {
    // Inverse rotation: -Yaw, -Pitch, -Roll in reverse order
    const cosY = Math.cos(-yawRad);
    const sinY = Math.sin(-yawRad);
    const x1 = v.x * cosY + v.z * sinY;
    const y1 = v.y;
    const z1 = -v.x * sinY + v.z * cosY;
    const cosP = Math.cos(-pitchRad);
    const sinP = Math.sin(-pitchRad);
    const x2 = x1;
    const y2 = y1 * cosP - z1 * sinP;
    const z2 = y1 * sinP + z1 * cosP;
    const cosR = Math.cos(-rollRad);
    const sinR = Math.sin(-rollRad);
    const x3 = x2 * cosR - y2 * sinR;
    const y3 = x2 * sinR + y2 * cosR;
    const z3 = z2;
    return { x: x3, y: y3, z: z3 };
}
/**
 * Computes geometric path through multi-layered armor plates including air gaps.
 */
export function traceMultiLayerGeometry(layers, baseThicknessMm, cosTheta) {
    const safeCos = Math.max(cosTheta, EPSILON);
    if (!layers || layers.length === 0) {
        const singlePath = baseThicknessMm / safeCos;
        return {
            totalGeometricPathMm: singlePath,
            totalAirGapMm: 0,
            layerTraces: [
                {
                    layerIndex: 0,
                    nominalThicknessMm: baseThicknessMm,
                    geometricPathLengthMm: singlePath,
                    airGapMm: 0,
                },
            ],
        };
    }
    let totalGeometricPathMm = 0;
    let totalAirGapMm = 0;
    const layerTraces = [];
    for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];
        const path = layer.thicknessMm / safeCos;
        totalGeometricPathMm += path;
        totalAirGapMm += layer.airGapMm;
        layerTraces.push({
            layerIndex: layer.layerIndex,
            nominalThicknessMm: layer.thicknessMm,
            geometricPathLengthMm: path,
            airGapMm: layer.airGapMm,
        });
    }
    return {
        totalGeometricPathMm,
        totalAirGapMm,
        layerTraces,
    };
}
//# sourceMappingURL=geometry.js.map